import { useEffect, useMemo, useState } from 'react'
import { chapterGuides, draftingWorkstreams, type ChapterGuide } from './data/chapter-guides'
import { chapterGuidesEn, draftingWorkstreamsEn } from './data/chapter-guides-en'
import { getOfficialUpdate, getRegimeNote, getRegimes, type Language } from './data/special-regimes'
import { getProspectusPrecedents } from './data/prospectus-precedents'
import { getChapterWorkbench } from './data/chapter-workbench'
import { getChapterTips, type TipKind } from './data/chapter-tips'

type View = 'start' | 'chapter' | 'tips' | 'workstreams' | 'regimes' | 'precedents'

const ui = {
  zh: {
    brand: '香港上市招股书起草参考',
    chapters: '章节目录',
    search: '搜索章节、主题或易错点',
    searchPlaceholder: '搜索：客户集中度、WVR、营运资金、展示文件…',
    noResults: '没有找到相关章节，请尝试更具体的业务或规则关键词。',
    results: (count: number) => `${count} 个相关章节`,
    chapterOf: (number: string, total: number) => `第 ${number} 章 / 共 ${total} 章`,
    scope: '章节起草范围',
    scopeHint: '本章应覆盖的对象、事实和支持文件',
    focus: '重点关注',
    focusHint: '起草和尽调过程中需要回答的问题',
    pitfalls: '易错误区',
    special: '特别提示',
    authorities: '规则与指引',
    authorityHint: '优先链接正式来源；使用时确认适用版本及生效日期',
    related: '关联章节回查',
    workstreamsTitle: '项目级前置事项',
    workstreamsSummary: '这些问题横跨多个章节，应在项目早期建立单独台账并持续更新。',
    projectWork: '项目级事项',
    startDesk: '起草起点',
    chapterTips: '章节 Tips',
    specialRegimes: '18A / 18C / WVR',
    precedentLibrary: '招股书范例库',
    openSource: '打开正式来源',
    sourceNote: 'Excel 用作原始覆盖框架；现行要求以正式规则、指引、监管沟通及已核实的项目事实为准。范例仅供起草结构参考，并非法律依据。',
    sectionNav: ['范围', '重点', '易错', '提示', '规则'],
    groups: [
      { id: 'front', label: '前置及说明文件', ids: ['cover', 'timetable', 'summary', 'definitions', 'waivers', 'issuer-info'] },
      { id: 'core', label: '核心披露', ids: ['risk', 'industry', 'regulations', 'history', 'business', 'directors', 'controlling', 'connected', 'substantial', 'capital', 'financial', 'proceeds'] },
      { id: 'offer', label: '发售安排', ids: ['cornerstone', 'underwriting', 'offering', 'apply'] },
      { id: 'appendices', label: '附录及交付', ids: ['financial-appendices', 'constitution', 'statutory', 'documents'] },
    ],
    regimeKicker: 'SPECIAL LISTING REGIMES',
    regimeTitle: '特殊上市制度核查',
    regimeIntro: '先判断适用路径，再把特殊要求落入具体章节。以下不是独立披露孤岛，而是贯穿封面、摘要、业务、财务、股本、发售及附录的核查矩阵。',
    gate: '准入判断',
    drafting: '落入招股书',
    regimeMistakes: '高风险遗漏',
    affected: '重点影响章节',
    latest: '最新规则',
    precedentKicker: 'PROSPECTUS PRECEDENTS',
    precedentTitle: '经典与近期招股书范例',
    precedentIntro: '按起草问题选择范例，不以篇幅或发行人名气作为模板。先看目录与披露逻辑，再回到现行规则和本项目事实。',
    learn: '适合参考',
    caution: '使用限制',
    openProspectus: '打开港交所文件',
    startKicker: 'DRAFTING DESK',
    startTitle: '从手上的工作开始',
    startIntro: '选择你现在要完成的工作，不需要从封面顺序阅读。',
    assignedChapter: '我被分配了一个章节',
    assignedHint: '直接搜索或从目录进入，先看工作包，再开始落笔。',
    checkRegime: '我要判断特殊上市路径',
    checkRegimeHint: '核对 WVR、18A、18C 的准入、披露及章节影响。',
    findPrecedent: '我要找一份可参考的招股书',
    findPrecedentHint: '按起草问题选择经典或近期范例。',
    closingWork: '我要做项目级核查或 closing',
    closingHint: '查看豁免、首次公开招股前投资、注册及展示等跨章节事项。',
    frequent: '常用章节',
    workbench: '本章工作包',
    workbenchHint: '先完成以下工作，再进入逐段起草',
    deliverable: '本轮应交付',
    startWith: '先做这几件事',
    askWhom: '向谁追问',
    evidence: '需要的底稿',
    mainlandBridge: '境内经验如何转化',
    completion: '本章完成度',
    copyPack: '复制工作包',
    copied: '已复制',
    resetProgress: '清除本章进度',
    checks: ['范围已经逐项覆盖', '重点问题均有事实及底稿支持', '易错点和关联章节已回查', '正式规则已打开并确认现行版本'],
    chapterResults: '章节',
    directResults: '直接入口',
    tipsKicker: 'MOBILE QUICK REFERENCE',
    tipsTitle: '章节实操 Tips',
    tipsIntro: '适合在手机上按章节快速查看：实操切入点、常见错误、市场范例、热点问题及对应正式规则。',
    chooseTipChapter: '选择感兴趣的章节',
    allTips: '全部',
    tipKinds: { practice: '实操', hot: '热点', precedent: '范例', rule: '规则' },
    doThis: '实际怎么做',
    sources: '对应来源',
    readChapter: '进入完整章节',
  },
  en: {
    brand: 'HK Prospectus Drafting Reference',
    chapters: 'Chapters',
    search: 'Search chapters, topics or common gaps',
    searchPlaceholder: 'Search: concentration, WVR, working capital, display documents…',
    noResults: 'No relevant chapter found. Try a more specific business or rule term.',
    results: (count: number) => `${count} relevant chapter${count === 1 ? '' : 's'}`,
    chapterOf: (number: string, total: number) => `Chapter ${number} of ${total}`,
    scope: 'Drafting scope',
    scopeHint: 'Subjects, facts and supporting documents this chapter should cover',
    focus: 'Key drafting focus',
    focusHint: 'Questions to answer through drafting and due diligence',
    pitfalls: 'Common gaps',
    special: 'Special note',
    authorities: 'Rules and guidance',
    authorityHint: 'Prefer official sources; confirm applicability, version and effective date',
    related: 'Cross-check related chapters',
    workstreamsTitle: 'Project-wide workstreams',
    workstreamsSummary: 'These issues cut across chapters and should have their own live workstream from the start of the transaction.',
    projectWork: 'Project-wide matters',
    startDesk: 'Drafting start',
    chapterTips: 'Chapter tips',
    specialRegimes: '18A / 18C / WVR',
    precedentLibrary: 'Prospectus precedents',
    openSource: 'Open official source',
    sourceNote: 'The Excel is used as the original coverage framework. Current rules, guidance, regulatory discussions and verified transaction facts prevail. Precedents are drafting references, not legal authority.',
    sectionNav: ['Scope', 'Focus', 'Gaps', 'Note', 'Rules'],
    groups: [
      { id: 'front', label: 'Front matter', ids: ['cover', 'timetable', 'summary', 'definitions', 'waivers', 'issuer-info'] },
      { id: 'core', label: 'Core disclosure', ids: ['risk', 'industry', 'regulations', 'history', 'business', 'directors', 'controlling', 'connected', 'substantial', 'capital', 'financial', 'proceeds'] },
      { id: 'offer', label: 'Offering', ids: ['cornerstone', 'underwriting', 'offering', 'apply'] },
      { id: 'appendices', label: 'Appendices and delivery', ids: ['financial-appendices', 'constitution', 'statutory', 'documents'] },
    ],
    regimeKicker: 'SPECIAL LISTING REGIMES',
    regimeTitle: 'Special listing regime checks',
    regimeIntro: 'Determine the route first, then carry the special requirements into the affected chapters. These regimes run through the cover, summary, business, financials, capital, offer and appendices.',
    gate: 'Entry analysis',
    drafting: 'Carry into the prospectus',
    regimeMistakes: 'High-risk gaps',
    affected: 'Priority chapters',
    latest: 'Latest rules',
    precedentKicker: 'PROSPECTUS PRECEDENTS',
    precedentTitle: 'Classic and recent prospectuses',
    precedentIntro: 'Choose a precedent by drafting issue, not length or issuer profile. Study its contents and disclosure logic, then return to current rules and your facts.',
    learn: 'Useful for',
    caution: 'Limit',
    openProspectus: 'Open HKEX document',
    startKicker: 'DRAFTING DESK',
    startTitle: 'Start from the work in front of you',
    startIntro: 'Choose the task you need to finish now. You do not need to read from the cover in sequence.',
    assignedChapter: 'I have been assigned a chapter',
    assignedHint: 'Search or enter from the contents, review the work pack, then draft.',
    checkRegime: 'I need to assess a special listing route',
    checkRegimeHint: 'Check WVR, 18A and 18C entry, disclosure and chapter impact.',
    findPrecedent: 'I need a prospectus precedent',
    findPrecedentHint: 'Choose a classic or recent filing by drafting issue.',
    closingWork: 'I am doing project checks or closing',
    closingHint: 'Review waivers, pre-IPO investment, registration and display workstreams.',
    frequent: 'Frequently used chapters',
    workbench: 'Chapter work pack',
    workbenchHint: 'Complete these before drafting paragraph by paragraph',
    deliverable: 'Deliverable for this round',
    startWith: 'Start here',
    askWhom: 'Who to ask',
    evidence: 'Evidence to obtain',
    mainlandBridge: 'Using PRC experience',
    completion: 'Chapter completion',
    copyPack: 'Copy work pack',
    copied: 'Copied',
    resetProgress: 'Clear chapter progress',
    checks: ['Scope covered item by item', 'Focus questions supported by facts and workpapers', 'Common gaps and related chapters cross-checked', 'Official rules opened and current version confirmed'],
    chapterResults: 'Chapters',
    directResults: 'Direct entries',
    tipsKicker: 'MOBILE QUICK REFERENCE',
    tipsTitle: 'Chapter drafting tips',
    tipsIntro: 'A mobile-first chapter view of practical starting points, common errors, market precedents, current issues and official rules.',
    chooseTipChapter: 'Choose a chapter',
    allTips: 'All',
    tipKinds: { practice: 'Practice', hot: 'Current issue', precedent: 'Precedent', rule: 'Rules' },
    doThis: 'What to do',
    sources: 'Sources',
    readChapter: 'Open full chapter',
  },
}

const searchText = (language: Language, guide: ChapterGuide) => {
  const workbench = getChapterWorkbench(language, guide)
  const regimeNote = getRegimeNote(language, guide.id)
  const tips = getChapterTips(language, guide)
  return [guide.label, guide.englishLabel, guide.summary, ...guide.scope, ...guide.focus.flatMap((item) => [item.title, item.detail]), ...guide.pitfalls, guide.alert.title, guide.alert.detail, workbench.deliverable, ...workbench.start, ...workbench.ask.flatMap((item) => [item.role, item.text]), ...workbench.evidence, workbench.bridge, ...tips.flatMap((tip) => [tip.title, tip.detail, tip.action]), ...(regimeNote ? [...regimeNote.codes, regimeNote.title, regimeNote.detail] : [])]
    .join(' ')
    .toLowerCase()
}

const initialRoute = () => {
  const value = window.location.hash.replace(/^#\/?/, '')
  if (value.startsWith('chapter/')) return { view: 'chapter' as View, activeId: value.split('/')[1] || 'cover' }
  if (value.startsWith('tips/')) return { view: 'tips' as View, activeId: value.split('/')[1] || 'business' }
  if (['workstreams', 'regimes', 'precedents', 'start'].includes(value)) return { view: value as View, activeId: 'cover' }
  return { view: 'start' as View, activeId: 'cover' }
}

function App() {
  const route = initialRoute()
  const [language, setLanguage] = useState<Language>(() => (localStorage.getItem('hk-guide-language') === 'en' ? 'en' : 'zh'))
  const [activeId, setActiveId] = useState(route.activeId)
  const [query, setQuery] = useState('')
  const [navOpen, setNavOpen] = useState(false)
  const [view, setView] = useState<View>(route.view)

  const guides = language === 'zh' ? chapterGuides : chapterGuidesEn
  const workstreams = language === 'zh' ? draftingWorkstreams : draftingWorkstreamsEn
  const copy = ui[language]
  const active = guides.find((item) => item.id === activeId) || guides[0]
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return { chapters: [] as ChapterGuide[], regimes: false, precedents: false }
    const terms = term.split(/\s+/).filter(Boolean)
    const matchesTerms = (text: string) => terms.every((item) => text.includes(item))
    const regimeText = getRegimes(language).map((item) => JSON.stringify(item)).join(' ').toLowerCase()
    const precedentText = getProspectusPrecedents(language).map((item) => JSON.stringify(item)).join(' ').toLowerCase()
    return {
      chapters: guides.filter((guide) => matchesTerms(searchText(language, guide))),
      regimes: matchesTerms(regimeText),
      precedents: matchesTerms(precedentText),
    }
  }, [guides, language, query])

  useEffect(() => {
    localStorage.setItem('hk-guide-language', language)
    document.documentElement.lang = language === 'zh' ? 'zh-HK' : 'en'
  }, [language])

  useEffect(() => {
    const syncFromHash = () => {
      const next = initialRoute()
      setView(next.view)
      if (next.view === 'chapter' || next.view === 'tips') setActiveId(next.activeId)
    }
    window.addEventListener('hashchange', syncFromHash)
    return () => window.removeEventListener('hashchange', syncFromHash)
  }, [])

  const showView = (next: View) => {
    setQuery('')
    setView(next)
    setNavOpen(false)
    window.location.hash = next
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const chooseChapter = (id: string) => {
    setActiveId(id)
    setQuery('')
    setView('chapter')
    setNavOpen(false)
    window.location.hash = `chapter/${id}`
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const chooseTips = (id = activeId) => {
    setActiveId(id)
    setQuery('')
    setView('tips')
    setNavOpen(false)
    window.location.hash = `tips/${id}`
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changeLanguage = (next: Language) => {
    setLanguage(next)
    setQuery('')
  }

  return (
    <div className="guide-shell">
      <header className="topbar">
        <button className="brand" onClick={() => showView('start')} aria-label={copy.brand}>
          <span>HK</span><strong>{copy.brand}</strong>
        </button>
        <label className="top-search">
          <span aria-hidden="true">⌕</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.searchPlaceholder} aria-label={copy.search} />
          {query && <button onClick={() => setQuery('')} aria-label={language === 'zh' ? '清除搜索' : 'Clear search'}>×</button>}
        </label>
        <div className="language-switch" aria-label={language === 'zh' ? '语言选择' : 'Language'}>
          <button className={language === 'zh' ? 'active' : ''} onClick={() => changeLanguage('zh')} aria-pressed={language === 'zh'}>中</button>
          <button className={language === 'en' ? 'active' : ''} onClick={() => changeLanguage('en')} aria-pressed={language === 'en'}>EN</button>
        </div>
        <button className="mobile-nav" onClick={() => setNavOpen((value) => !value)} aria-expanded={navOpen} aria-controls="chapter-nav">
          {language === 'zh' ? '目录' : 'Menu'}
        </button>
      </header>

      <div className="workspace">
        <aside id="chapter-nav" className={`chapter-nav ${navOpen ? 'open' : ''}`} aria-label={copy.chapters}>
          <div className="nav-heading"><span>{copy.chapters}</span><small>{guides.length}</small></div>
          <div className="utility-nav">
            <button className={view === 'start' ? 'active' : ''} onClick={() => showView('start')}><span>⌂</span><strong>{copy.startDesk}</strong></button>
            <button className={view === 'tips' ? 'active' : ''} onClick={() => chooseTips(activeId === 'cover' ? 'business' : activeId)}><span>◎</span><strong>{copy.chapterTips}</strong></button>
            <button className={view === 'workstreams' ? 'active' : ''} onClick={() => showView('workstreams')}><span>＋</span><strong>{copy.projectWork}</strong></button>
            <button className={view === 'regimes' ? 'active' : ''} onClick={() => showView('regimes')}><span>§</span><strong>{copy.specialRegimes}</strong></button>
            <button className={view === 'precedents' ? 'active' : ''} onClick={() => showView('precedents')}><span>↗</span><strong>{copy.precedentLibrary}</strong></button>
          </div>
          <nav>
            {copy.groups.map((group) => (
              <div className="nav-group" key={group.id}>
                <p>{group.label}</p>
                {group.ids.map((id) => {
                  const chapter = guides.find((item) => item.id === id)
                  if (!chapter) return null
                  return <button key={chapter.id} className={view === 'chapter' && chapter.id === active.id ? 'active' : ''} onClick={() => chooseChapter(chapter.id)}><span>{chapter.number}</span><strong>{chapter.label}</strong></button>
                })}
              </div>
            ))}
          </nav>
        </aside>

        <main>
          {query ? (
            <SearchResults matches={matches} copy={copy} onChoose={chooseChapter} onShowView={showView} />
          ) : view === 'start' ? (
            <StartPage copy={copy} guides={guides} onChoose={chooseChapter} onShowView={showView} />
          ) : view === 'tips' ? (
            <TipsPage language={language} copy={copy} guides={guides} activeId={activeId} onSelect={chooseTips} onReadChapter={chooseChapter} />
          ) : view === 'workstreams' ? (
            <WorkstreamPage copy={copy} items={workstreams} />
          ) : view === 'regimes' ? (
            <RegimePage language={language} copy={copy} guides={guides} onChoose={chooseChapter} />
          ) : view === 'precedents' ? (
            <PrecedentPage language={language} copy={copy} />
          ) : (
            <ChapterPage language={language} guide={active} guides={guides} copy={copy} onCrossCheck={chooseChapter} />
          )}
          <footer>{copy.sourceNote}</footer>
        </main>
      </div>
    </div>
  )
}

type Copy = typeof ui.zh

function SearchResults({ matches, copy, onChoose, onShowView }: { matches: { chapters: ChapterGuide[]; regimes: boolean; precedents: boolean }; copy: Copy; onChoose: (id: string) => void; onShowView: (view: View) => void }) {
  const total = matches.chapters.length + Number(matches.regimes) + Number(matches.precedents)
  return (
    <section className="search-page">
      <div className="search-page-heading"><p>{total ? copy.results(total) : copy.noResults}</p></div>
      {(matches.regimes || matches.precedents) && <div className="direct-results"><h2>{copy.directResults}</h2>
        {matches.regimes && <button onClick={() => onShowView('regimes')}><span>§</span><div><strong>{copy.specialRegimes}</strong><p>{copy.checkRegimeHint}</p></div><em>→</em></button>}
        {matches.precedents && <button onClick={() => onShowView('precedents')}><span>↗</span><div><strong>{copy.precedentLibrary}</strong><p>{copy.findPrecedentHint}</p></div><em>→</em></button>}
      </div>}
      {matches.chapters.length > 0 && <><div className="result-label">{copy.chapterResults}</div><div className="search-result-list">{matches.chapters.map((guide) => <button key={guide.id} onClick={() => onChoose(guide.id)}><span>{guide.number}</span><div><strong>{guide.label}</strong><p>{guide.summary}</p></div><em>→</em></button>)}</div></>}
    </section>
  )
}

function StartPage({ copy, guides, onChoose, onShowView }: { copy: Copy; guides: ChapterGuide[]; onChoose: (id: string) => void; onShowView: (view: View) => void }) {
  const frequentIds = ['business', 'risk', 'financial', 'regulations', 'history', 'statutory']
  return (
    <section className="start-page">
      <header><p>{copy.startKicker}</p><h1>{copy.startTitle}</h1><div>{copy.startIntro}</div></header>
      <div className="task-grid">
        <article><span>01</span><h2>{copy.assignedChapter}</h2><p>{copy.assignedHint}</p><label><span>⌕</span><input onClick={() => document.querySelector<HTMLInputElement>('.top-search input')?.focus()} placeholder={copy.searchPlaceholder} readOnly /></label></article>
        <button onClick={() => onShowView('regimes')}><span>02</span><h2>{copy.checkRegime}</h2><p>{copy.checkRegimeHint}</p><em>→</em></button>
        <button onClick={() => onShowView('precedents')}><span>03</span><h2>{copy.findPrecedent}</h2><p>{copy.findPrecedentHint}</p><em>→</em></button>
        <button onClick={() => onShowView('workstreams')}><span>04</span><h2>{copy.closingWork}</h2><p>{copy.closingHint}</p><em>→</em></button>
      </div>
      <section className="frequent-chapters"><h2>{copy.frequent}</h2><div>{frequentIds.map((id) => {
        const guide = guides.find((item) => item.id === id)
        return guide ? <button key={id} onClick={() => onChoose(id)}><span>{guide.number}</span><strong>{guide.label}</strong><em>→</em></button> : null
      })}</div></section>
    </section>
  )
}

function ChapterPage({ language, guide, guides, copy, onCrossCheck }: { language: Language; guide: ChapterGuide; guides: ChapterGuide[]; copy: Copy; onCrossCheck: (id: string) => void }) {
  const regimeNote = getRegimeNote(language, guide.id)
  return (
    <>
      <div className="chapter-toolbar">
        <span>{copy.chapterOf(guide.number, guides.length)}</span>
        <nav aria-label={copy.chapters}>{copy.sectionNav.map((label, index) => <a key={label} href={`#section-${index + 1}`}>{label}</a>)}</nav>
      </div>
      <section className="chapter-header">
        {guide.label !== guide.englishLabel && <p>{guide.englishLabel}</p>}
        <h1>{guide.label}</h1><div>{guide.summary}</div>
      </section>
      {regimeNote && <aside className="regime-note"><div>{regimeNote.codes.map((code) => <span key={code}>{code}</span>)}</div><strong>{regimeNote.title}</strong><p>{regimeNote.detail}</p></aside>}
      <ChapterWorkPack language={language} guide={guide} copy={copy} />
      <section id="section-1" className="scope-panel" aria-labelledby="scope-title">
        <SectionTitle number="01" title={copy.scope} hint={copy.scopeHint} id="scope-title" />
        <ul>{guide.scope.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
      <section id="section-2" className="focus-section" aria-labelledby="focus-title">
        <SectionTitle number="02" title={copy.focus} hint={copy.focusHint} id="focus-title" />
        <div className="focus-grid">{guide.focus.map((item, index) => <article key={item.title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{item.title}</h3><p>{item.detail}</p></article>)}</div>
      </section>
      <section id="section-3" className="mistake-section" aria-labelledby="mistake-title">
        <SectionTitle number="03" title={copy.pitfalls} id="mistake-title" />
        <ol>{guide.pitfalls.map((item) => <li key={item}><span>×</span>{item}</li>)}</ol>
      </section>
      <section id="section-4" className="alert-panel"><p>04 · {copy.special}</p><h2>{guide.alert.title}</h2><div>{guide.alert.detail}</div></section>
      <section id="section-5" className="reference-section" aria-labelledby="rules-title">
        <SectionTitle number="05" title={copy.authorities} hint={copy.authorityHint} id="rules-title" />
        <div className="rule-list">{guide.rules.map((rule) => <a key={`${rule.label}-${rule.citation}`} href={rule.url} target="_blank" rel="noreferrer"><span>↗</span><div><strong>{rule.label}</strong><small>{rule.citation}</small></div></a>)}</div>
      </section>
      <section className="cross-checks">
        <p>{copy.related}</p><div>{guide.crossChecks.map((label) => {
          const target = guides.find((item) => item.label === label)
          return target ? <button key={label} onClick={() => onCrossCheck(target.id)}>{target.number} · {label}<span>→</span></button> : <span key={label}>{label}</span>
        })}</div>
      </section>
    </>
  )
}

function SectionTitle({ number, title, hint, id }: { number: string; title: string; hint?: string; id: string }) {
  return <div className="section-heading"><div><p>{number}</p><h2 id={id}>{title}</h2></div>{hint && <span>{hint}</span>}</div>
}

function ChapterWorkPack({ language, guide, copy }: { language: Language; guide: ChapterGuide; copy: Copy }) {
  const workbench = getChapterWorkbench(language, guide)
  const storageKey = `hk-guide-progress-${guide.id}`
  const [checks, setChecks] = useState<boolean[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]')
      return copy.checks.map((_, index) => Boolean(saved[index]))
    } catch {
      return copy.checks.map(() => false)
    }
  })
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]')
      setChecks(copy.checks.map((_, index) => Boolean(saved[index])))
    } catch {
      setChecks(copy.checks.map(() => false))
    }
  }, [storageKey, copy.checks])

  const updateCheck = (index: number) => {
    const next = checks.map((value, itemIndex) => itemIndex === index ? !value : value)
    setChecks(next)
    localStorage.setItem(storageKey, JSON.stringify(next))
  }

  const copyWorkPack = async () => {
    const text = [
      `${copy.workbench} — ${guide.label}`,
      `${copy.deliverable}: ${workbench.deliverable}`,
      `\n${copy.startWith}`,
      ...workbench.start.map((item) => `- ${item}`),
      `\n${copy.askWhom}`,
      ...workbench.ask.map((item) => `- ${item.role}: ${item.text}`),
      `\n${copy.evidence}`,
      ...workbench.evidence.map((item) => `- ${item}`),
      `\n${copy.mainlandBridge}: ${workbench.bridge}`,
    ].join('\n')
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable')
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      const field = document.createElement('textarea')
      field.value = text
      field.setAttribute('readonly', '')
      field.style.position = 'fixed'
      field.style.opacity = '0'
      document.body.appendChild(field)
      field.select()
      const copiedSuccessfully = document.execCommand('copy')
      document.body.removeChild(field)
      if (copiedSuccessfully) {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1600)
      }
    }
  }

  return (
    <section className="workpack">
      <header><div><p>{copy.workbench}</p><h2>{copy.workbenchHint}</h2></div><button onClick={copyWorkPack}>{copied ? copy.copied : copy.copyPack}</button></header>
      <div className="deliverable"><span>{copy.deliverable}</span><strong>{workbench.deliverable}</strong></div>
      <div className="workpack-grid">
        <article><h3>{copy.startWith}</h3><ol>{workbench.start.map((item) => <li key={item}>{item}</li>)}</ol></article>
        <article><h3>{copy.askWhom}</h3><ul>{workbench.ask.map((item) => <li key={`${item.role}-${item.text}`}><strong>{item.role}</strong><span>{item.text}</span></li>)}</ul></article>
        <article><h3>{copy.evidence}</h3><ul>{workbench.evidence.map((item) => <li key={item}>{item}</li>)}</ul></article>
      </div>
      <aside><span>{copy.mainlandBridge}</span><p>{workbench.bridge}</p></aside>
      <div className="progress-checks">
        <header><strong>{copy.completion}</strong><span>{checks.filter(Boolean).length} / {checks.length}</span></header>
        <div>{copy.checks.map((item, index) => <label key={item}><input type="checkbox" checked={checks[index]} onChange={() => updateCheck(index)} /><span>{item}</span></label>)}</div>
        {checks.some(Boolean) && <button onClick={() => { const next = checks.map(() => false); setChecks(next); localStorage.removeItem(storageKey) }}>{copy.resetProgress}</button>}
      </div>
    </section>
  )
}

function TipsPage({ language, copy, guides, activeId, onSelect, onReadChapter }: { language: Language; copy: Copy; guides: ChapterGuide[]; activeId: string; onSelect: (id: string) => void; onReadChapter: (id: string) => void }) {
  const [filter, setFilter] = useState<'all' | TipKind>('all')
  const guide = guides.find((item) => item.id === activeId) || guides.find((item) => item.id === 'business') || guides[0]
  const tips = getChapterTips(language, guide)
  const filtered = filter === 'all' ? tips : tips.filter((tip) => tip.kind === filter)
  const filters: Array<'all' | TipKind> = ['all', 'practice', 'hot', 'precedent', 'rule']

  useEffect(() => setFilter('all'), [guide.id])

  return (
    <section className="tips-page">
      <header><p>{copy.tipsKicker}</p><h1>{copy.tipsTitle}</h1><div>{copy.tipsIntro}</div></header>
      <div className="tips-controls">
        <label><span>{copy.chooseTipChapter}</span><select value={guide.id} onChange={(event) => onSelect(event.target.value)}>{guides.map((item) => <option key={item.id} value={item.id}>{item.number} · {item.label}</option>)}</select></label>
        <button onClick={() => onReadChapter(guide.id)}>{copy.readChapter} →</button>
      </div>
      <nav className="tip-filters" aria-label={copy.chapterTips}>{filters.map((kind) => {
        const label = kind === 'all' ? copy.allTips : copy.tipKinds[kind]
        const count = kind === 'all' ? tips.length : tips.filter((tip) => tip.kind === kind).length
        if (count === 0) return null
        return <button key={kind} className={filter === kind ? 'active' : ''} onClick={() => setFilter(kind)}>{label}<span>{count}</span></button>
      })}</nav>
      <div className="tip-list">{filtered.map((tip, index) => (
        <article className={`tip-card tip-${tip.kind}`} key={`${tip.kind}-${tip.title}`}>
          <header><span>{copy.tipKinds[tip.kind]}</span><small>{String(index + 1).padStart(2, '0')}</small></header>
          <h2>{tip.title}</h2><p>{tip.detail}</p>
          <aside><strong>{copy.doThis}</strong><p>{tip.action}</p></aside>
          {tip.sources.length > 0 && <div className="tip-sources"><strong>{copy.sources}</strong>{tip.sources.map((source) => <a key={`${source.label}-${source.citation}`} href={source.url} target="_blank" rel="noreferrer"><span>{source.label}</span><small>{source.citation}</small><em>↗</em></a>)}</div>}
        </article>
      ))}</div>
    </section>
  )
}

function WorkstreamPage({ copy, items }: { copy: Copy; items: typeof draftingWorkstreams }) {
  return <section className="workstreams-page"><header><p>PROJECT-WIDE</p><h1>{copy.workstreamsTitle}</h1><div>{copy.workstreamsSummary}</div></header><div className="workstream-grid">{items.map((item, index) => <article key={item.title}><span>{String(index + 1).padStart(2, '0')}</span><h2>{item.title}</h2><p>{item.detail}</p><a href={item.source.url} target="_blank" rel="noreferrer">{copy.openSource}<small>{item.source.citation}</small></a></article>)}</div></section>
}

function RegimePage({ language, copy, guides, onChoose }: { language: Language; copy: Copy; guides: ChapterGuide[]; onChoose: (id: string) => void }) {
  const update = getOfficialUpdate(language)
  return (
    <section className="library-page">
      <header><p>{copy.regimeKicker}</p><h1>{copy.regimeTitle}</h1><div>{copy.regimeIntro}</div></header>
      <article className="latest-update"><div><span>{copy.latest}</span><small>{update.date}</small></div><h2>{update.title}</h2><p>{update.summary}</p><ul>{update.points.map((point) => <li key={point}>{point}</li>)}</ul><a href={getOfficialUpdate(language).url} target="_blank" rel="noreferrer">{update.label} ↗</a></article>
      <div className="regime-list">{getRegimes(language).map((regime) => (
        <article className="regime-card" key={regime.code}>
          <header><span>{regime.code}</span><div><h2>{regime.title}</h2><p>{regime.subtitle}</p></div></header>
          <div className="regime-columns">
            <section><h3>{copy.gate}</h3><ul>{regime.gate.map((item) => <li key={item}>{item}</li>)}</ul></section>
            <section><h3>{copy.drafting}</h3><ul>{regime.drafting.map((item) => <li key={item}>{item}</li>)}</ul></section>
            <section className="regime-errors"><h3>{copy.regimeMistakes}</h3><ul>{regime.mistakes.map((item) => <li key={item}>{item}</li>)}</ul></section>
          </div>
          <div className="regime-footer"><div><small>{copy.affected}</small>{regime.chapterIds.map((id) => {
            const chapter = guides.find((item) => item.id === id)
            return chapter ? <button key={id} onClick={() => onChoose(id)}>{chapter.number} {chapter.label}</button> : null
          })}</div><nav>{regime.links.map((link) => <a key={link.citation} href={link.url} target="_blank" rel="noreferrer">{link.citation} ↗</a>)}</nav></div>
        </article>
      ))}</div>
    </section>
  )
}

function PrecedentPage({ language, copy }: { language: Language; copy: Copy }) {
  return (
    <section className="library-page">
      <header><p>{copy.precedentKicker}</p><h1>{copy.precedentTitle}</h1><div>{copy.precedentIntro}</div></header>
      <div className="precedent-grid">{getProspectusPrecedents(language).map((item) => (
        <article className="precedent-card" key={item.issuer}>
          <div className="precedent-meta"><strong>{item.year}</strong><div>{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div></div>
          <h2>{item.issuer}</h2><p>{item.reason}</p>
          <h3>{copy.learn}</h3><ul>{item.useFor.map((use) => <li key={use}>{use}</li>)}</ul>
          <aside><strong>{copy.caution}</strong>{item.caution}</aside>
          <a href={item.url} target="_blank" rel="noreferrer">{copy.openProspectus} ↗</a>
        </article>
      ))}</div>
    </section>
  )
}

export default App
